import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';
import { InvoiceSettingsPanel } from './InvoiceSettingsPanel';

export function InvoicePaymentSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/invoice-payment-settings');
      setSettings(res.data.settings || getDefaultSettings());
      setEligibleUsers(res.data.eligible_users || []);
    } catch (err) {
      setSettings(getDefaultSettings());
    }
    setLoading(false);
  };

  const getDefaultSettings = () => ({
    invoice_approver_enabled: false,
    default_invoice_approver: '',
    receive_invoices_by_email: false,
    email_type: 'generic',
    external_invoice_validator: '',
    allow_validator_log_invoice: false,
    using_ocr: false,
    auto_log_invoice: false,
    dynamic_invoicing: 'no',
    invoice_uniqueness: 'per_order',
    payment_type: 'per_item',
    enable_shipping_charges: false,
    shipping_charge_type: 'per_order',
    enable_order_quote: false
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/invoice-payment-settings', settings);
      toast.success('Invoice & payment settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl" data-testid="invoice-payment-settings-panel">
      <h1 className="text-2xl font-bold mb-1">Invoice and Payment</h1>
      <p className="text-sm text-muted-foreground mb-6">Invoice module settings, payment configuration, and approver assignments</p>

      {/* Invoice Module Settings (merged from InvoiceSettingsPanel) */}
      <div className="mb-6">
        <InvoiceSettingsPanel />
      </div>

      <hr className="mb-6" />

      {/* Section: Assign Invoice Approver */}
      <div className="border rounded-lg p-6 mb-6" data-testid="invoice-approver-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Assign Invoice Approver
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          This section will activate the Invoice approval in the system. To activate, switch the
          'Invoice Approver' button to 'Yes' to see the list of Invoice Approver(s). Select the
          user from the dropdown to act as a Default Invoice Approver.
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-48 text-sm">Invoice Approver</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={settings.invoice_approver_enabled}
                onCheckedChange={(v) => setSettings(p => ({ ...p, invoice_approver_enabled: v }))}
                data-testid="invoice-approver-toggle"
              />
              <span className={`text-sm font-medium ${settings.invoice_approver_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                {settings.invoice_approver_enabled ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
          {settings.invoice_approver_enabled && (
            <div className="flex items-center gap-4">
              <Label className="w-48 text-sm">Please Select Default Invoice Approver</Label>
              <select
                value={settings.default_invoice_approver || ''}
                onChange={(e) => setSettings(p => ({ ...p, default_invoice_approver: e.target.value }))}
                className="w-64 h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="default-invoice-approver-select"
              >
                <option value="">-- Select --</option>
                {eligibleUsers.map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Section 2: External Invoice - OCR */}
      <div className="border rounded-lg p-6 mb-6" data-testid="external-invoice-ocr-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          External Invoice - OCR
        </h3>

        {/* Receive Supplier Invoices by Email */}
        <div className="flex items-center gap-4 mb-6">
          <Label className="text-sm font-semibold">Receive Supplier Invoices by Email</Label>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.receive_invoices_by_email}
              onCheckedChange={(v) => setSettings(p => ({ ...p, receive_invoices_by_email: v }))}
              data-testid="receive-invoices-toggle"
            />
            <span className={`text-sm font-medium ${settings.receive_invoices_by_email ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.receive_invoices_by_email ? 'Yes' : 'No'}
            </span>
          </div>
        </div>

        {settings.receive_invoices_by_email && (
          <>
            {/* Email Type Radio Cards */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <label className={`flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
                settings.email_type === 'auto_generated' ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <input
                    type="radio" name="email_type" value="auto_generated"
                    checked={settings.email_type === 'auto_generated'}
                    onChange={(e) => setSettings(p => ({ ...p, email_type: e.target.value }))}
                    className="h-4 w-4 text-primary"
                    data-testid="email-type-auto"
                  />
                  <span className="text-sm font-medium">Auto Generated Email</span>
                </div>
                <span className="text-xs text-muted-foreground">Assign unique email address to each purchase order</span>
                <span className="text-xs text-primary mt-1 font-mono">e.g. 1071.12.eyvo@ebuyerassist.com</span>
              </label>
              <label className={`flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
                settings.email_type === 'generic' ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <input
                    type="radio" name="email_type" value="generic"
                    checked={settings.email_type === 'generic'}
                    onChange={(e) => setSettings(p => ({ ...p, email_type: e.target.value }))}
                    className="h-4 w-4 text-primary"
                    data-testid="email-type-generic"
                  />
                  <span className="text-sm font-medium">Generic Email</span>
                </div>
                <span className="text-xs text-muted-foreground">Use a single Company wide Account Payable Email address</span>
                <span className="text-xs text-primary mt-1 font-mono">e.g. eyvo@ebuyerassist.com</span>
              </label>
            </div>

            {/* Two-column layout: Left = Email description, Right = Validation/OCR */}
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column: Auto Generate Email description */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Auto Generate Email -</h4>
                  <p className="text-xs text-muted-foreground">
                    The system automatically generates a unique Email ID for each Order visible on the Order screen
                    and the Order PDF. This Email ID will be used for communication between the system and the supplier.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Upon generating the Email ID, the supplier will send an Invoice PDF to that specific Email ID
                    corresponding to the particular Order. The application will then attach the Invoice under the
                    Order's "Invoices {'>'} Attached Invoice(s)" section, and the Buyer will receive an email notification
                    regarding the attached invoice.
                  </p>
                </div>

                {/* Auto Log Invoice Using OCR */}
                <div className="flex items-center gap-3 pt-2">
                  <Switch
                    checked={settings.using_ocr}
                    onCheckedChange={(v) => setSettings(p => ({ ...p, using_ocr: v }))}
                    data-testid="using-ocr-toggle"
                  />
                  <span className="text-sm font-medium">Auto Log Invoice Using OCR</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Upon receiving the Invoice PDF, the system will use the OCR functionality to scan and extract
                  the Invoice Number, Invoice Date, and Invoice Amount. Once the data extraction is successful,
                  the system will generate and log the Invoice and notify the Buyer of the Order.
                </p>
              </div>

              {/* Right Column: Generate Email / Validator / OCR Details */}
              <div className="space-y-4">
                {/* Generate Email Section */}
                <div className="border rounded-lg p-4 bg-muted/10">
                  <h4 className="text-sm font-semibold mb-2">Generate Email</h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    The system will generate a one-time unique email address specific to the company. The supplier
                    can use this email address to submit an Invoice in PDF format. Once the system receives the
                    Invoice PDF, it will transfer it to the "External Invoice" section and notify the External
                    Invoice Validator.
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    The validator will carefully examine the Invoice, ensure a proper match is found, and subsequently log the Invoice.
                  </p>

                  {/* External Invoice Validator */}
                  <div className="flex items-center gap-3 mb-2">
                    <Label className="text-xs font-semibold whitespace-nowrap">External Invoice Validator</Label>
                    <select
                      value={settings.external_invoice_validator || ''}
                      onChange={(e) => setSettings(p => ({ ...p, external_invoice_validator: e.target.value }))}
                      className="w-44 h-8 rounded-md border border-input bg-background px-2 text-xs"
                      data-testid="external-invoice-validator-select"
                    >
                      <option value="">-- Select --</option>
                      {eligibleUsers.map(u => (
                        <option key={u.user_id} value={u.user_id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-3">
                    Select Additional External Invoice Validators
                  </p>

                  <p className="text-xs text-muted-foreground mb-3">
                    The External Invoice Validator receives all the submitted invoices through the generic Email ID.
                    Invoices that do not have a matching order or are not logged in the system due to certain business
                    rules are flagged, and the External Invoice Validator is responsible for manually correcting these
                    flagged invoices.
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    The Validator can access all unprocessed invoices through the Invoice Matching section, Menu {'>'} External Invoice menu.
                  </p>

                  {/* Allow external invoice validator to Log Invoice */}
                  <div className="flex items-center gap-3 mb-2">
                    <Switch
                      checked={settings.allow_validator_log_invoice}
                      onCheckedChange={(v) => setSettings(p => ({ ...p, allow_validator_log_invoice: v }))}
                      data-testid="allow-validator-log-toggle"
                    />
                    <span className="text-xs font-medium">Allow external invoice validator to Log Invoice.</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    To enable External Invoice Validator to log the Invoice after matching the Invoice PDF with an Order, toggle on this option.
                  </p>
                </div>

                {/* Using OCR - highlighted box */}
                <div className="border rounded-lg p-4 bg-amber-50/50 dark:bg-amber-950/10 border-amber-200/50">
                  <div className="flex items-center gap-3 mb-2">
                    <Switch
                      checked={settings.using_ocr}
                      onCheckedChange={(v) => setSettings(p => ({ ...p, using_ocr: v }))}
                    />
                    <span className="text-xs font-semibold">Using OCR</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Upon receiving an Invoice PDF in the "External Invoice" section, the system will attempt to scan
                    and extract the PO Number using OCR functionality and match it within the system. If the PO Number
                    matches, the system will extract additional Invoice details such as Invoice Number, Invoice Date,
                    and Invoice Amount.
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    It will mark the Invoice as a successful match and notify the "External Invoice Validator". However,
                    if the PO Number does not match, the "External Invoice Validator" will manually match the Invoice with
                    the correct PO and Log the Invoice if granted with proper permission.
                  </p>

                  {/* Auto Log Invoice - nested inside OCR box */}
                  <div className="border-t pt-3 mt-2">
                    <div className="flex items-center gap-3 mb-2">
                      <Switch
                        checked={settings.auto_log_invoice}
                        onCheckedChange={(v) => setSettings(p => ({ ...p, auto_log_invoice: v }))}
                        data-testid="auto-log-invoice-toggle"
                      />
                      <span className="text-xs font-semibold">Auto Log Invoice</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      When the "Auto Log Invoice" feature is activated, the system will automate the logging process of
                      Invoices. After receiving an Invoice in PDF format through the Generic Email ID, the system will
                      perform OCR scanning and match it with the corresponding PO number in the system.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Once a successful match is made, the Auto Log Invoice functionality will automatically log the
                      Invoice on behalf of the Buyer associated with the Order. Simultaneously, both the External Invoice
                      Validator and the Buyer will be notified of the logged Invoice.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Section 3: Internal Invoice - OCR */}
      <div className="border rounded-lg p-6 mb-6" data-testid="internal-invoice-ocr-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">3</span>
          Internal Invoice - OCR
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          This setting switches ON the Invoice Optical Character Recognition (OCR) process.
          Enabling this will display a new menu option under Order {'>'} Invoices {'>'} Invoice Recording/Review {'>'} Menu {'>'} Log Invoice with OCR.
          It allows the buyer to manually select an invoice which 'reads' the Invoice Number, Invoice Date
          and the Invoice Amount and gives the buyer an option of creating the invoice record.
        </p>
        <div className="flex items-center gap-4">
          <Label className="w-48 text-sm">Dynamic Invoicing</Label>
          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio" name="dynamic_invoicing" value="no"
                checked={settings.dynamic_invoicing === 'no'}
                onChange={(e) => setSettings(p => ({ ...p, dynamic_invoicing: e.target.value }))}
                className="h-4 w-4 text-primary"
                data-testid="dynamic-invoicing-no"
              />
              <span className="text-sm">No</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio" name="dynamic_invoicing" value="yes"
                checked={settings.dynamic_invoicing === 'yes'}
                onChange={(e) => setSettings(p => ({ ...p, dynamic_invoicing: e.target.value }))}
                className="h-4 w-4 text-primary"
                data-testid="dynamic-invoicing-yes"
              />
              <span className="text-sm">Yes</span>
            </label>
          </div>
        </div>
      </div>

      {/* Section 4: Invoice Number Uniqueness */}
      <div className="border rounded-lg p-6 mb-6" data-testid="invoice-uniqueness-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">4</span>
          Invoice Number Uniqueness
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          This section will activate the type of invoice system you need to define in your system.
        </p>
        <div className="flex items-start gap-8">
          <Label className="w-48 text-sm pt-2">Please Select Invoice Type</Label>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio" name="invoice_uniqueness" value="per_order"
                checked={settings.invoice_uniqueness === 'per_order'}
                onChange={(e) => setSettings(p => ({ ...p, invoice_uniqueness: e.target.value }))}
                className="h-4 w-4 text-primary"
                data-testid="invoice-uniqueness-per-order"
              />
              <span className="text-sm">Set Unique Invoice Per Order</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio" name="invoice_uniqueness" value="per_supplier"
                checked={settings.invoice_uniqueness === 'per_supplier'}
                onChange={(e) => setSettings(p => ({ ...p, invoice_uniqueness: e.target.value }))}
                className="h-4 w-4 text-primary"
                data-testid="invoice-uniqueness-per-supplier"
              />
              <span className="text-sm">Set Unique Invoice Per Supplier</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio" name="invoice_uniqueness" value="per_system"
                checked={settings.invoice_uniqueness === 'per_system'}
                onChange={(e) => setSettings(p => ({ ...p, invoice_uniqueness: e.target.value }))}
                className="h-4 w-4 text-primary"
                data-testid="invoice-uniqueness-per-system"
              />
              <span className="text-sm">Set Unique Invoice Per System</span>
            </label>
          </div>
        </div>
      </div>

      {/* Section 5: Payment Type */}
      <div className="border rounded-lg p-6 mb-6" data-testid="payment-type-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">5</span>
          Payment Type
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          This section will log payments into the system against each item of the invoice
          using one of the following payment types.
        </p>
        <div className="flex items-start gap-8">
          <Label className="w-48 text-sm pt-2">Please Select Payment Type</Label>
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio" name="payment_type" value="per_item"
                checked={settings.payment_type === 'per_item'}
                onChange={(e) => setSettings(p => ({ ...p, payment_type: e.target.value }))}
                className="h-4 w-4 text-primary mt-0.5"
                data-testid="payment-type-per-item"
              />
              <div>
                <span className="text-sm font-medium">Payment Per Item</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Payments are logged against each item. Example: If an invoice has 3 items for $100 each,
                  the user has to log payment against each item.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio" name="payment_type" value="reducing"
                checked={settings.payment_type === 'reducing'}
                onChange={(e) => setSettings(p => ({ ...p, payment_type: e.target.value }))}
                className="h-4 w-4 text-primary mt-0.5"
                data-testid="payment-type-reducing"
              />
              <div>
                <span className="text-sm font-medium">Payment on Reducing Method</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Payments are logged against each item in reducing method. Example: If an invoice has 3 items
                  of $100 each and a partial payment of $250 is made, the system will assign $100 each to the
                  first and second item and the remaining $50 will be assigned to the last, third item.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio" name="payment_type" value="ratio"
                checked={settings.payment_type === 'ratio'}
                onChange={(e) => setSettings(p => ({ ...p, payment_type: e.target.value }))}
                className="h-4 w-4 text-primary mt-0.5"
                data-testid="payment-type-ratio"
              />
              <div>
                <span className="text-sm font-medium">Payment on Ratio Method</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Payments are logged against each item in ratio. Example: If an invoice has 3 items
                  of $100 each and a partial payment of $150 is made, the system will assign $50 against each item.
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Section 6: Shipping Charges */}
      <div className="border rounded-lg p-6 mb-6" data-testid="shipping-charges-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">6</span>
          Shipping Charges
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Select the checkbox for 'Shipping Charges' if you want to add shipping charges after order is ISSUED.
        </p>
        <div className="flex items-center gap-4">
          <Label className="w-48 text-sm">Shipping Charges</Label>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.enable_shipping_charges}
              onCheckedChange={(v) => setSettings(p => ({ ...p, enable_shipping_charges: v }))}
              data-testid="enable-shipping-charges-toggle"
            />
            <span className={`text-sm font-medium ${settings.enable_shipping_charges ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.enable_shipping_charges ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Section 7: Order - Quote */}
      <div className="border rounded-lg p-6 mb-6" data-testid="order-quote-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">7</span>
          Order - Quote
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          This section will read the pdf file through OCR and create an order in the system.
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          To activate, please switch the 'Order - Quote' button to 'Yes'.
        </p>
        <div className="flex items-center gap-4">
          <Label className="w-48 text-sm">Order - Quote</Label>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.enable_order_quote}
              onCheckedChange={(v) => setSettings(p => ({ ...p, enable_order_quote: v }))}
              data-testid="enable-order-quote-toggle"
            />
            <span className={`text-sm font-medium ${settings.enable_order_quote ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.enable_order_quote ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="invoice-payment-save-btn">
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
